<template>
  <div class="container-fluid py-4">
    <div class="row justify-content-center">
      <div class="col-lg-10 col-md-12">
        <div class="card">
          <div class="card-body p-4">
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2">Loading details...</p>
            </div>

            <template v-else>
            <!-- Assignment Details Box -->
            <div class="border border-dark p-4 mb-4 text-sm rounded" v-if="displayAssignmentDetails">
              <div class="mb-2">
                <strong>LOCATION:</strong> {{ displayAssignmentDetails.location }}
              </div>
              <div class="mb-2">
                <strong>YOUR EMAIL:</strong> {{ displayAssignmentDetails.email }}
              </div>
              <div class="mb-2">
                <strong>ASSIGNMENT GROUP:</strong> {{ displayAssignmentDetails.assignmentGroup }}
              </div>
              <div class="mb-2">
                <strong>ASSIGNMENT GROUP ID:</strong> {{ displayAssignmentDetails.assignmentGroupId }}
              </div>
              <div class="mb-0">
                <strong>ASSIGNMENT DATE:</strong> {{ displayAssignmentDetails.assignmentDate }}
              </div>
            </div>

            <!-- Unavailable Notice -->
            <div v-if="unavailable" class="alert alert-warning d-flex align-items-center gap-2 mb-4">
              <i class="material-icons">info</i>
              <span>{{ unavailableMessage }}</span>
            </div>

            <!-- User Message -->
            <div class="mb-4">
              <p class="mb-2">Hello <strong>{{ displayUserName }}</strong> ,</p>
              <p class="mb-2" v-if="!unavailable">{{ displayMessage }}</p>
              <p class="mb-2" v-if="displayAdditionalMessage">{{ displayAdditionalMessage }}</p>
              <p class="mb-0">Thank you.</p>
            </div>

            <!-- Close Page Button -->
            <!-- <div class="mb-4">
              <MaterialButton @click="closePage" color="secondary" variant="fill" size="md">
                <i class="material-icons me-2">close</i>
                Close Page
              </MaterialButton>
            </div> -->

            <!-- Divider -->
            <hr class="my-4" />

            <!-- Did You Know Section -->
            <div class="mb-4" v-if="showDidYouKnow">
              <h5 class="fw-bold mb-3">Did You Know?</h5>
              <p class="mb-3">{{ displayDidYouKnowText }}</p>
              <p class="mb-2" v-if="displayDidYouKnowSteps && displayDidYouKnowSteps.length > 0">To setup an external
                calendar, follow the steps below:</p>
              <ul class="mb-3" v-if="displayDidYouKnowSteps && displayDidYouKnowSteps.length > 0">
                <li v-for="(step, index) in displayDidYouKnowSteps" :key="index" class="mb-2">{{ step }}</li>
              </ul>
              <p class="mb-2" v-if="showFeedback">
                You can report issues, send comments or make suggestions by using the steps below:
              </p>
              <ul class="mb-3" v-if="showFeedback">
                <li class="mb-2">click on the 'Request Assistance' button.</li>
                <li class="mb-2">send a message to support@churchscheduling.com</li>
              </ul>
              <p class="mb-0" v-if="showFeedback">
                Your feedback is critical to the improvement of this application.
              </p>
            </div>

            <!-- Login Link -->
            <router-link to="/sign-in">
              <MaterialButton class="mt-4">
                Click here to Login
              </MaterialButton>
            </router-link>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import MaterialButton from '@/components/common/MaterialButton.vue'
import api from '@/config/api'

const route = useRoute()
const toast = useToast()

// State
const loading = ref(true)
const assignmentDetails = ref({
  location: '',
  email: '',
  assignmentGroup: '',
  assignmentGroupId: '',
  assignmentDate: ''
})
const userName = ref('')
const message = ref('')
const additionalMessage = ref('')
const unavailable = ref(false)
const unavailableMessage = ref('')
const showDidYouKnow = ref(true)
const showFeedback = ref(false)
const didYouKnowText = ref('')
const didYouKnowSteps = ref([])

// Mock fallback data
const mockAssignmentDetails = {
  location: 'CS Test Church -> Chicago main Branch',
  email: 'sharjeelahmed@distrocode.com',
  assignmentGroup: 'altar server 67 @ Sunday Morning Service -main',
  assignmentGroupId: '39669',
  assignmentDate: 'Dec 28, 2025 10:00:00 AM'
}
const mockUserName = 'Sharjeel Ahmed Khan'
const mockMessage = 'The instance workgroup date you are responding to has already passed.'
const mockAdditionalMessage = 'There is no further action required for this assignment.'
const mockDidYouKnowText = 'You can setup external calendars like google, yahoo to display your upcoming assignments.'
const mockDidYouKnowSteps = [
  "log into the application and click on the 'my Profile/Preferences' button from the Home page",
  "copy the custom 'iCal URL' link",
  "paste the copied link into the appropriate calendar feed url field for the external calendar"
]

// Computed display values (prefer API, fallback to mock)
const displayAssignmentDetails = computed(() =>
  assignmentDetails.value && Object.values(assignmentDetails.value).some(v => v)
    ? assignmentDetails.value
    : mockAssignmentDetails
)
const displayUserName = computed(() => userName.value || mockUserName)
const displayMessage = computed(() => message.value || mockMessage)
const displayAdditionalMessage = computed(() => additionalMessage.value || mockAdditionalMessage)
const displayDidYouKnowText = computed(() => didYouKnowText.value || mockDidYouKnowText)
const displayDidYouKnowSteps = computed(() =>
  (didYouKnowSteps.value && didYouKnowSteps.value.length > 0) ? didYouKnowSteps.value : mockDidYouKnowSteps
)

// Process email response (auto on mount)
const processEmailResponse = async () => {
  const encodedString = route.params.encodedString
  if (!encodedString) return

  try {
    const response = await api.get(`/account/emailresponse/cs/${encodedString}`)
    if (response.data?.unavailable) {
      unavailable.value = true
      unavailableMessage.value = response.data.message || 'This assignment is no longer available.'
      return
    }
    if (response.data?.message) {
      toast.info(response.data.message, { position: 'top-right' })
    }
  } catch (error) {
    console.error('Error processing email response:', error)
    const errData = error.response?.data
    if (errData?.unavailable) {
      unavailable.value = true
      unavailableMessage.value = errData.message || 'This assignment is no longer available.'
      return
    }
    const errMsg = errData?.message || error.message || 'Failed to process email response.'
    toast.error(errMsg, { position: 'top-right' })
  }
}

// Fetch page details
const fetchEmailResponseDetails = async () => {
  const encodedString = route.params.encodedString
  if (!encodedString) {
    loading.value = false
    return
  }

  try {
    loading.value = true
    const response = await api.get(`/account/emailresponse-details/cs/${encodedString}`)

    if (response.data) {
      const data = response.data

      if (data.assignmentDetails) {
        assignmentDetails.value = {
          location: data.assignmentDetails.location || '',
          email: data.assignmentDetails.email || '',
          assignmentGroup: data.assignmentDetails.assignment_group || '',
          assignmentGroupId: data.assignmentDetails.assignment_group_id || '',
          assignmentDate: data.assignmentDetails.assignment_date || ''
        }
      }

      userName.value = data.username || ''
      message.value = data.message || ''
      additionalMessage.value = data.additionalMessage || ''
      if (data.unavailable && !unavailable.value) {
        unavailable.value = true
        unavailableMessage.value = data.message || 'This assignment is no longer available.'
      }
      if (data.showDidYouKnow != null) showDidYouKnow.value = data.showDidYouKnow
      if (data.showFeedback != null) showFeedback.value = data.showFeedback
      if (data.didYouKnowText != null) didYouKnowText.value = data.didYouKnowText
      if (data.didYouKnowSteps && Array.isArray(data.didYouKnowSteps)) didYouKnowSteps.value = data.didYouKnowSteps
    }
  } catch (error) {
    console.error('Error fetching email response details:', error)
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await processEmailResponse()
  await fetchEmailResponseDetails()
})
</script>
